import { useQuery } from '@tanstack/react-query'

import { TANSTACK_QUERY_KEY } from '@/constants/tanstack-query'
import { IHttpError } from '@/data/core'
import { isBtcChain } from '@/utils'
import { WalletContextType } from '@/wallets'

export function useAllPublicKeys(connectedWallets: Record<string, WalletContextType>) {
  const addresses = Object.values(connectedWallets).map((e) => e.address)
  return useQuery<Array<{ address: string; publicKey: string }>, IHttpError>({
    queryKey: [TANSTACK_QUERY_KEY.WALLET.GET_WALLET_PUBLIC_KEY, addresses.join(',')],
    queryFn: async () => {
      const pubkeys = await Promise.all(
        Object.values(connectedWallets)
          .filter((wallet): wallet is WalletContextType => Boolean(wallet))
          .map(async (wallet) => {
            try {
              const publicKey = await wallet.getCompressedPublicKey()
              return {
                address: wallet.address,
                publicKey,
              }
            } catch (error) {
              console.error('Failed to get compressed public key', error)
              return null
            }
          })
      )
      return pubkeys.filter(Boolean) as Array<{ address: string; publicKey: string }>
    },
    gcTime: 30_000,
    staleTime: 30_000,
    enabled: addresses?.length > 0,
  })
}

// because change btc wallet type (taproot, native segwit) dont trigger any event => we will manually fetch address
export function useAllWalletAddresses(connectedWallets: Record<string, WalletContextType>) {
  const addresses = Object.values(connectedWallets).map((e) => e.address)

  return useQuery<{ network: string; address: string }[], IHttpError>({
    queryKey: [TANSTACK_QUERY_KEY.WALLET.GET_BTC_WALLET_ADDRESS],
    queryFn: async () => {
      const entries = Object.entries(connectedWallets)
      const updates: Array<{ network: string; address: string }> = []

      for (const [network, walletContext] of entries) {
        if (!walletContext.wallet || !isBtcChain(network)) continue

        try {
          const address = await walletContext.wallet.getAddress(network)

          const currentWallet = connectedWallets[network]
          if (currentWallet?.address !== address) {
            updates.push({ network, address })
          }
        } catch {}
      }
      return updates
    },
    enabled: addresses?.length > 0,
    refetchInterval: 4000,
  })
}
